import {
  emitCustomEvent,
  useCustomEventListener,
} from 'infrastructure/hooks/utils';

import type { ModuleType } from 'infrastructure/enums';

// CALL END
interface ICallEndedEventDetails {
  patientGuid: string;
  callDuration: number;
  module: ModuleType;
  callId: string;
  /**
   * Present when the contact ENDED without ever connecting, other than through
   * the agent's own End/Cancel — the "could not connect" case the panel has to
   * explain. `reason` is the Amazon agent error-state name the failed call left
   * behind (FailedConnectCustomer, LineEngagedCustomer, …), or null when
   * Connect reported none.
   */
  failure: { reason: string | null } | null;
}

const CALL_ENDED_EVENT_NAME = 'callEnded';

export const emitCallEndedEvent = ({
  callDuration,
  patientGuid,
  module,
  callId,
  failure,
}: ICallEndedEventDetails) => {
  emitCustomEvent<ICallEndedEventDetails>(CALL_ENDED_EVENT_NAME, {
    callDuration,
    patientGuid,
    module,
    callId,
    failure,
  });
};

export const useCallEndedEventListener = (
  callback: (event: CustomEvent<ICallEndedEventDetails>) => void,
) => {
  useCustomEventListener<ICallEndedEventDetails>(
    CALL_ENDED_EVENT_NAME,
    callback,
  );
};

// CALL CONNECTING
// Fired from contact.onConnecting, i.e. while the patient's phone is ringing.
// callStarted only fires on answer, which is too late for anything that has to
// be on screen during the ring.
interface ICallConnectingEventDetails {}

const CALL_CONNECTING_EVENT_NAME = 'callConnecting';

export const emitCallConnectingEvent = () => {
  emitCustomEvent<ICallConnectingEventDetails>(CALL_CONNECTING_EVENT_NAME, {});
};

export const useCallConnectingEventListener = (
  callback: (event: CustomEvent<ICallConnectingEventDetails>) => void,
) => {
  useCustomEventListener<ICallConnectingEventDetails>(
    CALL_CONNECTING_EVENT_NAME,
    callback,
  );
};

// CALL END
interface ICallStartedEventDetails {}

const CALL_STARTED_EVENT_NAME = 'callStarted';

export const emitCallStartedEvent = () => {
  emitCustomEvent<ICallStartedEventDetails>(CALL_STARTED_EVENT_NAME, {});
};

export const useCallStartedEventListener = (
  callback: (event: CustomEvent<ICallStartedEventDetails>) => void,
) => {
  useCustomEventListener<ICallStartedEventDetails>(
    CALL_STARTED_EVENT_NAME,
    callback,
  );
};

// CALL DASHBOARD CALL STARTED
interface ICallDashboardCallStartedEventDetails {}

const CALL_DASHBOARD_CALL_STARTED_EVENT_NAME = 'callDashboardCallStarted';

export const emitCallDashboardCallStartedEvent = () => {
  emitCustomEvent<ICallDashboardCallStartedEventDetails>(
    CALL_DASHBOARD_CALL_STARTED_EVENT_NAME,
    {},
  );
};

export const useCallDashboardCallStartedEventListener = (
  callback: (event: CustomEvent<ICallDashboardCallStartedEventDetails>) => void,
) => {
  useCustomEventListener<ICallDashboardCallStartedEventDetails>(
    CALL_DASHBOARD_CALL_STARTED_EVENT_NAME,
    callback,
  );
};

// CALL DASHBOARD CALL END
interface ICallDashboardCallEndedEventDetails {}

const CALL_DASHBOARD_CALL_ENDED_EVENT_NAME = 'callDashboardCallEnded';

export const emitCallDashboardCallEndedEvent = () => {
  emitCustomEvent<ICallDashboardCallEndedEventDetails>(
    CALL_DASHBOARD_CALL_ENDED_EVENT_NAME,
    {},
  );
};

export const useCallDashboardCallEndedEventListener = (
  callback: (event: CustomEvent<ICallDashboardCallEndedEventDetails>) => void,
) => {
  useCustomEventListener<ICallDashboardCallEndedEventDetails>(
    CALL_DASHBOARD_CALL_ENDED_EVENT_NAME,
    callback,
  );
};
